import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { AuthService } from "@domain/auth/authService";
import { Environment } from "@core/config/environment";

interface ApiRequestConfig extends InternalAxiosRequestConfig {
	upToDateToken?: boolean;
}

const isAbsoluteUrl = (url?: string): boolean => !!url && /^https?:\/\//.test(url);

export class Api {
	private readonly instance: AxiosInstance;

	constructor(
		private authService: AuthService,
		private readonly admin?: "admin"
	) {
		this.instance = axios.create({
			headers: {
				"x-api-version": "1.0",
			},
		});
		this.initializeInteceptors();
	}

	get<T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.instance.get<T>(url, config) as Promise<R>;
	}

	post<T = unknown, R = AxiosResponse<T>>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<R> {
		return this.instance.post<T>(url, data, config) as Promise<R>;
	}

	put<T = unknown, R = AxiosResponse<T>>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<R> {
		return this.instance.put<T>(url, data, config) as Promise<R>;
	}

	delete<T = unknown, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.instance.delete<T>(url, config) as Promise<R>;
	}

	private initializeInteceptors(): void {
		this.instance.interceptors.request.use((config) => ({
			...config,
			url: isAbsoluteUrl(config.url)
				? config.url
				: `${this.admin ? Environment.API_BASE_URL_ADMIN : Environment.API_BASE_URL}${config.url}`,
		}));

		this.instance.interceptors.request.use(async (config) => {
			const token = await this.authService.getToken();
			if (token && !isAbsoluteUrl(config.url)) {
				config.headers.set("Authorization", `Bearer ${token}`);
			}
			return config;
		});

		this.instance.interceptors.response.use(undefined, async (error: unknown) => {
			const axiosError = error as AxiosError;
			if (!axiosError.config) {
				return Promise.reject({ error, code: axiosError.response?.status });
			}

			const config = axiosError.config as ApiRequestConfig;
			if (!config.upToDateToken && this.authService.hasUser.get() && axiosError.response?.status === 401) {
				console.log("401 Error, refreshing token ...");
				await this.authService.refreshToken();
				config.upToDateToken = true;
				return this.instance.request(config);
			}

			return Promise.reject({ error, code: axiosError.response?.status });
		});
	}
}
